import React from 'react';
import {
  BrowseFilter,
  DishFirstSegmentFilter,
  HierarchicalAvailableCuisineCategoryFragment,
} from 'src/gqlReactTypings.generated.d';
import { useShowConsumerHeaderPersistentBanner } from 'src/pages/consumer/components/header-v2/useShowConsumerHeaderPersistentBanner';
import { pickSubcuisineIcon } from 'src/pages/consumer/explore/components/cuisine-filters/pickSubcuisineIcon';
import { UseExploreUrl } from 'src/pages/consumer/explore/useExploreUrl';
import { WavyEdge } from 'src/shared/design-system/WavyEdge';
import { ifProp } from 'src/shared/styled-utils/ifProp';
import styled, { css } from 'styled-components';
import { match } from 'ts-pattern';
import { Icon as IconComponent } from 'web-common/src/shared/design-system/components/Icon';
import { Colors, Font, Spacing } from 'web-common/src/shared/styles';
import { DIETARY_TO_DISPLAY_INFO, PROTEIN_TO_DISPLAY_INFO } from '../MoreFilters/consts';
import { DishFirstExploreWidthLimit } from '../SharedStyles';
import { HeroContent } from './styles';

const BrowseContainer = styled.div<{ addSpacing?: boolean }>`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  background: ${Colors.KOHLRABI_LITE_30};

  ${ifProp(
    'addSpacing',
    css`
      padding: ${Spacing.TRIPLE} ${Spacing.NONE} ${Spacing.NONE} ${Spacing.NONE};
    `
  )};
`;

const BrowseTitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: left;
  flex-direction: row;
`;

const BrowseTitleText = styled.h1`
  font-size: 32px; // One-off
  line-height: ${Font.LineHeight.XXL};
  font-weight: ${Font.Weight.BOLD};
  margin: ${Spacing.HALF} ${Spacing.NONE} ${Spacing.NONE};
`;

const IconContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-right: ${Spacing.HALF};
`;

interface BrowseHeroProps {
  className?: string;
  displayName: string;
  icon?: React.ReactNode;
  addSpacing?: boolean;
}

const BrowseHero: React.FC<BrowseHeroProps> = ({ className, icon, displayName, addSpacing = false }) => (
  <BrowseContainer addSpacing={addSpacing} className={className}>
    <DishFirstExploreWidthLimit>
      <HeroContent>
        <BrowseTitleContainer>
          {icon && <IconContainer>{icon}</IconContainer>}
          <BrowseTitleText>{displayName}</BrowseTitleText>
        </BrowseTitleContainer>
        <WavyEdge />
      </HeroContent>
    </DishFirstExploreWidthLimit>
  </BrowseContainer>
);

// Icon dimensions are different, described here accordingly.
const dietaryProteinIconDimensions = { width: '24', height: '24' };

type IProps = {
  className?: string;
  cuisines: HierarchicalAvailableCuisineCategoryFragment[];
  filters: UseExploreUrl<DishFirstSegmentFilter>;
};

export const BrowseFilterHero: React.FC<IProps> = ({ className, filters, cuisines }) => {
  const { enabled: promoBannerEnabled } = useShowConsumerHeaderPersistentBanner();

  if (!filters.browseFilter) return null;

  const result = match(filters.browseFilter)
    .with(BrowseFilter.Cuisine, () => {
      const filterValue = filters.cuisineFilters[0];
      const cuisine = cuisines.find((c) => c.id === filterValue);
      if (!cuisine) return null;

      // Show the icon / text if the selected cuisine is a primary cuisine, otherwise use a primary ancestor.
      const filterDisplayInfo = pickSubcuisineIcon(cuisine);
      const { title } = cuisine;

      return {
        Icon: <IconComponent name={filterDisplayInfo.hoverIcon} size={56} />,
        displayText: `${title} cuisine`,
      };
    })
    .with(BrowseFilter.Dietary, () => {
      const filterValue = filters.dietaryFilters[0];
      if (!filterValue) return null;

      const { Icon, title } = DIETARY_TO_DISPLAY_INFO[filterValue];
      return { Icon: <Icon {...dietaryProteinIconDimensions} />, displayText: `${title} dishes` };
    })
    .with(BrowseFilter.Protein, () => {
      const filterValue = filters.proteinFilters[0];
      if (!filterValue) return null;

      const { Icon, title } = PROTEIN_TO_DISPLAY_INFO[filterValue];
      return { Icon: <Icon {...dietaryProteinIconDimensions} />, displayText: `${title} dishes` };
    })
    .otherwise(() => null);

  return result ? (
    <BrowseHero
      icon={result.Icon}
      className={className}
      displayName={result.displayText}
      addSpacing={promoBannerEnabled}
    />
  ) : null;
};
